import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth as useWorkOSAuth } from "@workos-inc/authkit-react";
import { AuthService } from "@/services/auth-service";
import { API_BASE } from "@/config/api";
import type { User } from "@/schemas";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  updateUser: (updates: Partial<User>) => void;
  login: (params?: { provider?: string }) => Promise<void>;
  signup: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  updateUser: () => {},
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
});

interface WorkOSAuthLike {
  user?: unknown;
  loading?: boolean;
  isLoading?: boolean;
  isAuthenticated?: boolean;
  signIn?: (params?: Record<string, unknown>) => Promise<void> | void;
  signUp?: (params?: Record<string, unknown>) => Promise<void> | void;
  signOut?: (options?: { navigate?: boolean; returnTo?: string }) => Promise<void> | void;
  getAccessToken?: () => Promise<string | null>;
}

function getWorkOSName(user: unknown): string | undefined {
  if (!user || typeof user !== "object") return undefined;
  const obj = user as Record<string, unknown>;

  const directName = obj.name;
  if (typeof directName === "string" && directName.trim() !== "") return directName.trim();

  const first = typeof obj.first_name === "string" ? obj.first_name.trim() : "";
  const last = typeof obj.last_name === "string" ? obj.last_name.trim() : "";
  const combined = [first, last].filter(Boolean).join(" ").trim();
  if (combined) return combined;

  const nestedUser = obj.user;
  if (nestedUser && typeof nestedUser === "object") {
    const nestedObj = nestedUser as Record<string, unknown>;
    const nestedName = nestedObj.name;
    if (typeof nestedName === "string" && nestedName.trim() !== "") return nestedName.trim();
  }

  return undefined;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const workos = useWorkOSAuth() as unknown as WorkOSAuthLike;
  const [user, setUser] = useState<User | null>(null);
  const [syncing, setSyncing] = useState(true);

  const workosLoading = Boolean(workos.loading ?? workos.isLoading);
  const workosAuthenticated = Boolean(workos.isAuthenticated ?? workos.user);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const syncSession = useCallback(async () => {
    if (AuthService.isForceLoggedOut()) {
      AuthService.removeToken();
      setUser(null);
      setSyncing(false);
      return;
    }

    if (
      window.location.pathname === "/login" &&
      new URLSearchParams(window.location.search).has("error")
    ) {
      setSyncing(false);
      return;
    }

    if (!workosAuthenticated) {
      AuthService.removeToken();
      setUser(null);
      setSyncing(false);
      return;
    }

    try {
      const accessToken = await workos.getAccessToken?.();
      if (!accessToken) {
        AuthService.removeToken();
        setUser(null);
        setSyncing(false);
        return;
      }

      AuthService.setToken(accessToken);

      const name = getWorkOSName(workos.user);
      const response = await fetch(`${API_BASE}/auth/session/sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        AuthService.removeToken();
        setUser(null);
        setSyncing(false);
        let errorCode = response.status === 409 ? "identity_conflict" : "authentication_failed";
        try {
          const payload = (await response.json()) as { code?: string; error?: string };
          if (payload?.code) {
            errorCode = payload.code;
          } else if (payload?.error?.toLowerCase().includes("identity email is missing")) {
            errorCode = "identity_email_missing";
          }
        } catch {
          // Ignore parse errors and fallback to status-based code.
        }
        window.location.href = `/login?error=${errorCode}`;
        return;
      }

      const payload = await response.json();
      setUser(payload.user ?? null);
    } catch (error) {
      console.error("Error synchronizing auth session:", error);
      AuthService.removeToken();
      setUser(null);
      window.location.href = "/login?error=authentication_failed";
    } finally {
      setSyncing(false);
    }
  }, [workos, workosAuthenticated]);

  useEffect(() => {
    if (workosLoading) {
      setSyncing(true);
      return;
    }

    setSyncing(true);
    void syncSession();
  }, [workosLoading, syncSession]);

  const login = useCallback(
    async (params?: { provider?: string }) => {
      AuthService.setForceLoggedOut(false);
      if (!workos.signIn) {
        throw new Error("WorkOS signIn is not available");
      }

      if (params?.provider) {
        await workos.signIn({ provider: params.provider });
        return;
      }

      await workos.signIn();
    },
    [workos],
  );

  const logout = useCallback(async () => {
    AuthService.setForceLoggedOut(true);
    AuthService.removeToken();
    setUser(null);

    try {
      if (workos.signOut) {
        await workos.signOut({ navigate: false });
      }
    } finally {
      window.location.replace("/");
    }
  }, [workos]);

  const signup = useCallback(async () => {
    AuthService.setForceLoggedOut(false);
    if (!workos.signUp) {
      throw new Error("WorkOS signUp is not available");
    }
    await workos.signUp();
  }, [workos]);

  const value = useMemo(
    () => ({
      user,
      loading: workosLoading || syncing,
      isAuthenticated: !!user,
      updateUser,
      login,
      signup,
      logout,
    }),
    [user, workosLoading, syncing, updateUser, login, signup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
